/**
 * Wave 5c2 — the staff-role collapse mapping (client-safe, no server imports).
 *
 * The 16 operational Hasura roles died into ONE `staff` role whose database
 * permissions consult `user_effective_capabilities`. The MATRIX vocabulary
 * (tenant_roles.key / property_members.role_key — 'manager', 'front_desk',
 * custom keys…) is untouched and keeps driving CAPABILITIES and UI wording;
 * only the AUTH plane (auth.user_roles / default_role / x-hasura-role)
 * collapses. Every place that turns a matrix role into a Hasura role goes
 * through this module so the mapping cannot drift.
 */

/** Hasura roles that carry ceiling authority — never collapsed. */
export const CEILING_AUTH_ROLES = ['tenant_admin', 'owner'] as const

/** The single operational Hasura role after the collapse. */
export const STAFF_AUTH_ROLE = 'staff'

/**
 * The Hasura roles the staff-management surface may assign after the
 * collapse. (Pre-collapse this listed all 16 operational roles.)
 */
export const ASSIGNABLE_AUTH_ROLES = ['tenant_admin', 'owner', STAFF_AUTH_ROLE] as const

/**
 * The 16 retired operational Hasura roles. Kept ONLY so cleanup paths
 * (offboarding, role reconciliation) may still REVOKE leftover rows — they
 * are never assignable again.
 */
export const LEGACY_COLLAPSED_ROLES = [
  'accountant', 'cashier', 'cost_controller', 'department_head', 'front_desk',
  'housekeeping', 'kitchen_staff', 'maintenance', 'manager', 'outlet_manager',
  'pos_admin', 'pos_reporting', 'property_admin', 'purchasing_officer',
  'server', 'storekeeper',
] as const

const CEILING_SET: ReadonlySet<string> = new Set(CEILING_AUTH_ROLES)

/**
 * Map a MATRIX role key (tenant_roles.key / property_members.role_key,
 * including custom keys) to the Hasura AUTH role it implies.
 */
export function toHasuraAuthRole(matrixRoleKey: string): string {
  return CEILING_SET.has(matrixRoleKey) ? matrixRoleKey : STAFF_AUTH_ROLE
}
